import { create } from 'zustand'
import { SessionMachine } from '@/voice/machine'
import type { RejectedTransition, SessionEvent } from '@/voice/machine'
import type {
  SessionState as MachineState,
  Speaker,
  VoiceCapabilities,
  VoiceError,
  VoiceEvent,
} from '@/voice/types'

/**
 * The live state of a conversation. Never persisted: when the app closes, the
 * conversation is over.
 *
 * Two things here are load-bearing and easy to get wrong.
 *
 * The input and output levels are kept apart. With one blended number, a noisy
 * room makes an idle assistant look like it is talking, and the only signal in
 * the whole interface that tells the user whose turn it is stops working.
 *
 * `muted` and `held` are flags, not states. Mute stops the microphone while
 * the assistant keeps talking; hold suspends the whole exchange. Collapsing
 * them into one control forces a person who wants to stop the assistant to
 * also give up their own turn.
 */

export interface TranscriptTurn {
  id: string
  speaker: Speaker
  text: string
  at: number
}

/** How many finished turns to keep. A session is a conversation, not an archive. */
const MAX_TURNS = 60

interface SessionStoreState {
  state: MachineState
  muted: boolean
  held: boolean
  inputLevel: number
  outputLevel: number
  /** Finished turns, oldest first. */
  turns: TranscriptTurn[]
  /**
   * The unfinished tail of what the user is saying. Kept out of `turns`
   * because a hypothesis that keeps changing is not a record of anything, and
   * putting it in the screen-reader live region would make it re-announce the
   * whole growing sentence on every word.
   */
  interim: string
  capabilities: VoiceCapabilities | null
  error: VoiceError | null
  /** Whether the typed input panel is open. */
  typing: boolean
  /** Set when the conversation is inside the consent step, which needs a visible choice. */
  awaitingConsent: boolean
  startedAt: number | null

  send(event: SessionEvent): MachineState | null
  applyVoiceEvent(event: VoiceEvent): void
  setMuted(muted: boolean): void
  setHeld(held: boolean): void
  setTyping(typing: boolean): void
  setAwaitingConsent(awaiting: boolean): void
  setCapabilities(capabilities: VoiceCapabilities): void
  clearError(): void
  reset(): void

  /** The most recent thing Namu said. Drives the caption band. */
  lastNamuTurn(): TranscriptTurn | null
  /** The most recent thing the user said. */
  lastUserTurn(): TranscriptTurn | null
  /** Rejected machine transitions, surfaced in diagnostics. */
  rejectedTransitions(): readonly RejectedTransition[]
}

let turnCounter = 0
function nextTurnId(): string {
  turnCounter += 1
  return `turn-${turnCounter}`
}

export const useSessionStore = create<SessionStoreState>()((setState, getState) => {
  const machine = new SessionMachine()

  return {
    state: machine.current,
    muted: false,
    held: false,
    inputLevel: 0,
    outputLevel: 0,
    turns: [],
    interim: '',
    capabilities: null,
    error: null,
    typing: false,
    awaitingConsent: false,
    startedAt: null,

    send(event) {
      const next = machine.send(event)
      if (next === null) return null
      setState({
        state: next,
        // Levels belong to the state that produced them. Carrying a stale
        // level across a transition leaves the orb lit after the mic closed.
        ...(next === 'listening' || next === 'user_speaking' ? {} : { inputLevel: 0 }),
        ...(next === 'model_speaking' ? {} : { outputLevel: 0 }),
        ...(event === 'START' ? { startedAt: Date.now() } : {}),
      })
      return next
    },

    applyVoiceEvent(event) {
      switch (event.type) {
        case 'level': {
          // Only the speaker who currently has the floor moves their meter.
          const current = getState().state
          if (event.source === 'user') {
            if (current === 'listening' || current === 'user_speaking' || current === 'interrupted') {
              setState({ inputLevel: event.value })
            }
          } else if (current === 'model_speaking') {
            setState({ outputLevel: event.value })
          }
          break
        }

        case 'transcript': {
          if (!event.final) {
            if (event.speaker === 'user') setState({ interim: event.text })
            break
          }
          const turn: TranscriptTurn = {
            id: nextTurnId(),
            speaker: event.speaker,
            text: event.text,
            at: Date.now(),
          }
          const turns = [...getState().turns, turn].slice(-MAX_TURNS)
          setState({ turns, ...(event.speaker === 'user' ? { interim: '' } : {}) })
          break
        }

        case 'speech_start':
          getState().send(event.speaker === 'user' ? 'USER_SPEAK_START' : 'AGENT_SPEAK_START')
          break

        case 'speech_end':
          if (event.speaker === 'user') {
            getState().send('USER_SPEAK_END')
          } else {
            getState().send(event.interrupted ? 'INTERRUPT' : 'AGENT_SPEAK_END')
          }
          break

        case 'connection':
          if (event.status === 'open') getState().send('CONNECTED')
          else if (event.status === 'reconnecting') getState().send('CONNECTION_LOST')
          break

        case 'error': {
          setState({ error: event.error })
          // A recoverable error is information, not a dead end: the session
          // stays alive and the UI shows a banner instead of a stop screen.
          if (!event.error.recoverable) getState().send('ERROR')
          break
        }

        case 'state':
          /*
           * Advisory, with one exception that is not.
           *
           * Listening, speaking and the connection each arrive as their own
           * event, and acting on this one as well would have the two fight:
           * the server calls the floor the user's again as soon as it has
           * finished generating, which is while the playback queue is still
           * draining, so honouring that here would take the orb off an answer
           * it is plainly still saying.
           *
           * Thinking is the state nothing else carries. There is no user
           * `speech_end` on the duplex path, because the model decides where
           * a turn ended rather than the client, so without this the orb went
           * on showing "listening" for the whole one to three seconds Namu's
           * own model takes and then jumped straight to speaking. The longest
           * part of a turn had no state at all.
           */
          if (event.state === 'processing') getState().send('PROCESSING_START')
          break
      }
    },

    setMuted(muted) {
      setState({ muted, ...(muted ? { inputLevel: 0 } : {}) })
    },

    setHeld(held) {
      setState({ held, ...(held ? { inputLevel: 0, outputLevel: 0 } : {}) })
    },

    setTyping(typing) {
      setState({ typing })
    },

    setAwaitingConsent(awaiting) {
      setState({ awaitingConsent: awaiting })
    },

    setCapabilities(capabilities) {
      setState({ capabilities })
    },

    clearError() {
      setState({ error: null })
    },

    reset() {
      machine.reset()
      setState({
        state: machine.current,
        muted: false,
        held: false,
        inputLevel: 0,
        outputLevel: 0,
        turns: [],
        interim: '',
        error: null,
        typing: false,
        awaitingConsent: false,
        startedAt: null,
      })
    },

    lastNamuTurn() {
      const turns = getState().turns
      for (let i = turns.length - 1; i >= 0; i -= 1) {
        const turn = turns[i]
        if (turn?.speaker === 'namu') return turn
      }
      return null
    },

    lastUserTurn() {
      const turns = getState().turns
      for (let i = turns.length - 1; i >= 0; i -= 1) {
        const turn = turns[i]
        if (turn?.speaker === 'user') return turn
      }
      return null
    },

    rejectedTransitions() {
      return machine.rejectedTransitions
    },
  }
})
